<script setup>
import { computed } from "vue";
import { formatAttendanceTime } from "@/utils/attendanceDateTime";

/**
 * Props
 * - rows: Array of attendance rows from API index() AFTER you switched to raw ISO fields.
 *         Each row should look like:
 *         {
 *           id, date (YYYY-MM-DD), status,
 *           check_in_at, check_out_at,
 *           break_minutes, late_minutes, overtime_minutes, total_work_minutes
 *         }
 * - loading: Boolean for table loading state
 */
const props = defineProps({
  rows: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
});

/* -----------------------------
   Helpers
--------------------------------*/
function toTs(v) {
  if (!v) return 0;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : 0;
}
function timeHHMM(v) {
  return formatAttendanceTime(v);
}
function dateLabel(yyyyMmDd) {
  if (!yyyyMmDd) return "-";
  try {
    // render with local timezone
    const d = new Date(yyyyMmDd + "T00:00:00");
    return d.toLocaleDateString();
  } catch {
    return yyyyMmDd;
  }
}
function minutesToLabel(m) {
  const minutes = parseInt(m || 0, 10);
  const h = Math.floor(minutes / 60);
  const mm = minutes % 60;
  return `${String(h).padStart(2, "0")}h ${String(mm).padStart(2, "0")}m`;
}

/* -----------------------------
   Columns (Ant Design Vue)
--------------------------------*/
const columns = [
  { sorter: false }, // selection / (to keep spacing like your old table)
  {
    title: "Date",
    dataIndex: "date",
    key: "date",
    sorter: { compare: (a, b) => toTs(a.date) - toTs(b.date) },
  },
  {
    title: "Check In",
    dataIndex: "check_in_at",
    key: "check_in",
    sorter: { compare: (a, b) => toTs(a.check_in_at) - toTs(b.check_in_at) },
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    sorter: {
      compare: (a, b) => {
        const x = (a.status || "").toLowerCase();
        const y = (b.status || "").toLowerCase();
        return x > y ? 1 : x < y ? -1 : 0;
      },
    },
  },
  {
    title: "Check Out",
    dataIndex: "check_out_at",
    key: "check_out",
    sorter: { compare: (a, b) => toTs(a.check_out_at) - toTs(b.check_out_at) },
  },
  {
    title: "Break",
    dataIndex: "break_minutes",
    key: "break",
    sorter: { compare: (a, b) => (a.break_minutes || 0) - (b.break_minutes || 0) },
  },
  {
    title: "Late",
    dataIndex: "late_minutes",
    key: "late",
    sorter: { compare: (a, b) => (a.late_minutes || 0) - (b.late_minutes || 0) },
  },
  {
    title: "Overtime",
    dataIndex: "overtime_minutes",
    key: "overtime",
    sorter: { compare: (a, b) => (a.overtime_minutes || 0) - (b.overtime_minutes || 0) },
  },
  {
    title: "Production Hours",
    dataIndex: "total_work_minutes",
    key: "production",
    sorter: { compare: (a, b) => (a.total_work_minutes || 0) - (b.total_work_minutes || 0) },
  },
];

// (optional) keep API rows as-is; let bodyCell format values
const dataSource = computed(() => props.rows || []);

// simple selection stubs to keep parity with your previous API
const rowSelection = {
  onChange: () => {},
  onSelect: () => {},
  onSelectAll: () => {},
};
</script>

<template>
  <a-table
    class="table datatable thead-light"
    :row-key="record => record.id || `${record.date}-${record.check_in_at}-${record.check_out_at}`"
    :columns="columns"
    :data-source="dataSource"
    :row-selection="rowSelection"
    :loading="loading"
  >
    <template #bodyCell="{ column, record }">
      <!-- Date -->
      <template v-if="column.key === 'date'">
        {{ dateLabel(record.date) }}
      </template>

      <!-- Check In / Check Out -->
      <template v-else-if="column.key === 'check_in'">
        {{ timeHHMM(record.check_in_at) }}
      </template>
      <template v-else-if="column.key === 'check_out'">
        <div class="d-flex flex-column align-items-start gap-1">
          <span>{{ timeHHMM(record.check_out_at) }}</span>
          <span
            v-if="record.auto_closed"
            class="badge badge-soft-warning badge-xs"
            title="System closed this punch at shift end because checkout was missing"
          >
            <i class="ti ti-clock-pause me-1"></i>Auto-closed
          </span>
        </div>
      </template>

      <!-- Status -->
      <template v-else-if="column.key === 'status'">
        <span
          class="d-inline-flex align-items-center"
          :class="[
            'badge',
            (record.status_label || record.status) === 'Present'
              ? 'badge-success-transparent'
              : (record.status_label || record.status) === 'Late'
              ? 'badge-warning-transparent'
              : (record.status_label || record.status) === 'Permission'
              ? 'badge-info-transparent'
              : 'badge-danger-transparent',
          ]"
        >
          <i class="ti ti-point-filled me-1"></i>{{ record.status_label || record.status || '-' }}
        </span>
      </template>

      <!-- Minutes → labels -->
      <template v-else-if="column.key === 'break'">
        {{ minutesToLabel(record.break_minutes) }}
      </template>
      <template v-else-if="column.key === 'late'">
        {{ record.late_minutes ? minutesToLabel(record.late_minutes) : '-' }}
      </template>
      <template v-else-if="column.key === 'overtime'">
        <span
          class="d-inline-flex align-items-center"
          :class="[
            'badge',
            (record.overtime_minutes || 0) > 0 ? 'badge-info' : 'badge-secondary-transparent',
          ]"
        >
          <i class="ti ti-clock-hour-11 me-1"></i>{{ minutesToLabel(record.overtime_minutes) }}
        </span>
      </template>
      <template v-else-if="column.key === 'production'">
        <span
          class="d-inline-flex align-items-center"
          :class="[
            'badge',
            (record.total_work_minutes || 0) >= 9 * 60
              ? 'badge-info'
              : (record.total_work_minutes || 0) < 7.5 * 60
              ? 'badge-danger'
              : 'badge-success',
          ]"
        >
          <i class="ti ti-clock-hour-11 me-1"></i>{{ minutesToLabel(record.total_work_minutes) }}
        </span>
      </template>
    </template>
  </a-table>
</template>
